import React, { FC } from 'react';
import clsx from 'clsx';

import { FormattedMessage } from '@futrli/localisation';

import { MasterfileButton } from '../../controls/masterfile-button';
import styles from './instructions.scss';

interface Props {
  openModal: () => void;
  hasCreatedImportSheet: boolean;
  hasSynced: boolean;
}

export const Instructions: FC<Props> = ({ hasCreatedImportSheet, hasSynced, openModal }) => {
  if (hasCreatedImportSheet && hasSynced) {
    return null;
  }

  const fadedSectionClasses = clsx({
    [styles.instructionSection]: true,
    [styles.instructionSectionFaded]: hasCreatedImportSheet && !hasSynced
  });

  return (
    <div className={clsx(styles.card, styles.instructionsCard)} data-testid="import-instructions">
      <div className={fadedSectionClasses}>
        <h1 className={clsx(styles.instructionsTitle, 'uiTypeHeader')}>
          <span className={styles.instructionsTitleNumber}>
            <FormattedMessage id="import.one_period" defaultMessage="1." />
          </span>
          <FormattedMessage id="common.click" defaultMessage="Click" />
        </h1>
        <p>
          <FormattedMessage
            id="import.instructions_text_1"
            defaultMessage="Get your Master file👇🏼 below and authorise Google when prompted."
          />
        </p>
        {!hasCreatedImportSheet && (
          <MasterfileButton onClick={openModal} testId="create-master-sheet-btn">
            <FormattedMessage id="import.create_master_sheet" defaultMessage="Create Master Sheet" />
          </MasterfileButton>
        )}
      </div>

      <div className={fadedSectionClasses}>
        <h1 className={clsx(styles.instructionsTitle, 'uiTypeHeader')}>
          <span className={styles.instructionsTitleNumber}>
            <FormattedMessage id="import.two_period" defaultMessage="2." />
          </span>
          <FormattedMessage id="common.favourite" defaultMessage="Favourite" />
        </h1>
        <p>
          <FormattedMessage
            id="import.instructions_text_2"
            defaultMessage="A GSheets file will be created which is now linked to this account and business. Favourite it in the browser for easy access."
          />
        </p>
      </div>

      <div className={styles.instructionSection}>
        <h1 className={clsx(styles.instructionsTitle, 'uiTypeHeader')}>
          <span className={styles.instructionsTitleNumber}>
            <FormattedMessage id="import.three_period" defaultMessage="3." />
          </span>
          <FormattedMessage id="common.choose" defaultMessage="Choose" />
        </h1>
        <p>
          <FormattedMessage
            id="import.instructions_text_3"
            defaultMessage="Return to Predict and click on any template you would like to use below 👇🏼"
          />
        </p>
      </div>

      <div className={styles.instructionSection}>
        <h1 className={clsx(styles.instructionsTitle, 'uiTypeHeader')}>
          <span className={styles.instructionsTitleNumber}>
            <FormattedMessage id="import.four_period" defaultMessage="4." />
          </span>
          <FormattedMessage id="common.copy" defaultMessage="Copy" />
        </h1>
        <p>
          <FormattedMessage
            id="import.instructions_text_4"
            defaultMessage="The GSheets template will open, ready for you to copy it into your Master file. "
          />
        </p>
      </div>

      <div className={styles.instructionSection}>
        <span className={styles.instructionsTitle}>
          <h1 className={clsx(styles.instructionsTitle, 'uiTypeHeader')}>
            <span className={styles.instructionsTitleNumber}>
              <FormattedMessage id="import.five_period" defaultMessage="5." />
            </span>
            <FormattedMessage id="import.go_nuts" defaultMessage="Go nuts" />
          </h1>
        </span>
        <p>
          <FormattedMessage
            id="import.instructions_text_5"
            defaultMessage="Once in the Master file, your Chart of Accounts will populate any relevant drop-downs you need for mapping, and your new template is ready to use."
          />
        </p>
      </div>

      <div className={styles.instructionSection}>
        <p className={styles.instructionsAside}>
          <FormattedMessage
            id="import.instructions_text_6"
            defaultMessage="*Each template you want to use must be copied into your Master file before editing."
          />
        </p>
      </div>
    </div>
  );
};
